import { SlidersHorizontal } from 'lucide-react';
import { AKCENT as SECTION_COLOR } from '../../../lib/spizirnaUi';

const STAVY = [
    { klic: 'potvrzeno', label: 'Rozhodnuto', barva: '#10B981' },
    { klic: 'ceka', label: 'Čeká', barva: SECTION_COLOR },
    { klic: 'ignorovat', label: 'Ignorováno', barva: '#8B8B9A' },
    { klic: 'chybi-protejsek', label: 'Chybí protějšek', barva: '#EF4444' },
];

/**
 * Třetí sloupec — kde na tom jsme.
 *
 * Měří se **podíl tržby, ne podíl řádků**. Jsou to dvě různá čísla (46 % řádků
 * a 66 % tržby) a to druhé je to, o co jde: dokud není nacenitelná tržba,
 * nespočítá se marže. Kdo vidí jen „hotovo 46 %", netuší, jestli zbývá práce
 * za dvacet minut, nebo za dva dny.
 *
 * Nic se tu needituje — podle normy §1e do třetího sloupce formulář nepatří.
 * Jediné tlačítko vede do slovníku variant, a to proto, že je to zdaleka
 * nejlevnější práce, jaká na téhle obrazovce je.
 */
export default function PrehledPanel({ prehled, styly, isDark, onOtevritVarianty }) {
    const { cardStyle, secondaryColor, mutedColor } = styly;

    if (!prehled) {
        return <div className="rounded-xl h-full" style={{ ...cardStyle }} />;
    }

    const hotovo = prehled.stavy.potvrzeno;
    const varianty = prehled.varianty;

    return (
        <div className="rounded-xl h-full flex flex-col overflow-hidden" style={{ ...cardStyle }}>
            <div className="px-4 pt-4 pb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: mutedColor }}>
                    Pokryto z tržby
                </div>
                <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-[30px] font-black leading-none tnum" style={{ color: 'var(--text-primary)' }}>
                        {hotovo.podil_trzby.toFixed(0)}
                    </span>
                    <span className="text-[16px] font-bold" style={{ color: mutedColor }}>%</span>
                </div>
                <div className="text-[11px] mt-1" style={{ color: secondaryColor }}>
                    {Math.round(hotovo.trzba).toLocaleString('cs-CZ')} z{' '}
                    {Math.round(prehled.trzba_celkem).toLocaleString('cs-CZ')} Kč
                    <span style={{ color: mutedColor }}> · {prehled.dni} dní</span>
                </div>

                <div className="mt-3 h-2 rounded-full overflow-hidden flex"
                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                    {STAVY.map(({ klic, barva }) => {
                        const p = prehled.stavy[klic]?.podil_trzby ?? 0;
                        return p > 0 ? <div key={klic} style={{ width: `${p}%`, background: barva }} /> : null;
                    })}
                </div>
            </div>

            <div className="px-4 pb-3 flex flex-col gap-2">
                {STAVY.map(({ klic, label, barva }) => {
                    const s = prehled.stavy[klic];
                    if (!s || (s.nazvu === 0 && klic !== 'ceka' && klic !== 'potvrzeno')) return null;
                    return (
                        <div key={klic} className="flex items-center gap-2">
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: barva, flexShrink: 0 }} />
                            <span className="text-[12px] flex-1" style={{ color: secondaryColor }}>{label}</span>
                            <span className="text-[12px] font-semibold tnum" style={{ color: 'var(--text-primary)' }}>
                                {s.nazvu}
                            </span>
                            <span className="text-[10.5px] tnum" style={{ width: 42, textAlign: 'right', color: mutedColor }}>
                                {s.podil_trzby.toFixed(0)} %
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Nejlevnější práce na obrazovce: pár řádků slovníku odemkne
                desítky názvů naráz. Bez tohohle čísla to vypadá jako vedlejší
                číselník, který může počkat. */}
            {varianty.blokuje_nazvu > 0 && (
                <div className="mx-3 mb-3 p-3 rounded-lg"
                    style={{ background: `${SECTION_COLOR}14`, border: `1px solid ${SECTION_COLOR}33` }}>
                    <div className="text-[11.5px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                        <b>{varianty.blokuje_nazvu}</b>{' '}
                        {varianty.blokuje_nazvu === 1 ? 'název čeká' : varianty.blokuje_nazvu < 5 ? 'názvy čekají' : 'názvů čeká'}
                        {' '}na variantu, kterou neznáme.
                    </div>
                    <button onClick={onOtevritVarianty}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11.5px] font-bold rounded-lg"
                        style={{
                            height: 30, cursor: 'pointer',
                            background: `${SECTION_COLOR}20`, border: `1px solid ${SECTION_COLOR}55`, color: SECTION_COLOR,
                        }}>
                        <SlidersHorizontal size={12} />
                        Doplnit slovník variant
                    </button>
                </div>
            )}

            <div className="mt-auto px-4 py-3 text-[10.5px] leading-relaxed"
                style={{
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                    color: mutedColor,
                }}>
                Počítá se jen to, co se za {prehled.dni} dní aspoň jednou prodalo.
                Názvy z archivu se nenabízejí — nejsou to práce, je to historie.
            </div>
        </div>
    );
}
